import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';

import { currentSportUnreadNotificationsCountFragment } from 'contexts/currentUser/queries';
import { useSportContext } from 'contexts/sport';
import { idFromObject } from 'gql/idFromObject';
import { useMutation } from 'hooks/graphql/useMutation';
import {
  importantNotificationCategories,
  notificationCategories,
} from 'lib/notifications';

import type {
  MarkNotificationsAsReadMutation,
  MarkNotificationsAsReadMutationVariables,
} from './__generated__/useMarkNotificationsAsRead.graphql';

const MARK_NOTIFICATIONS_AS_READ_MUTATION = gql`
  mutation MarkNotificationsAsReadMutation(
    $input: markNotificationsAsReadInput!
    $sport: Sport!
    $notificationCategories: [NotificationCategoryInput!]
    $importantNotificationCategories: [NotificationCategoryInput!]
  ) {
    markNotificationsAsRead(input: $input) {
      currentUser {
        slug
        ...CurrentUserProvider_currentSportUnreadNotificationsCount
      }
      notifications {
        id
        read
      }
      errors {
        path
        message
        code
      }
    }
  }
  ${currentSportUnreadNotificationsCountFragment}
` as TypedDocumentNode<
  MarkNotificationsAsReadMutation,
  MarkNotificationsAsReadMutationVariables
>;

export const useMarkNotificationsAsRead = () => {
  const [markNotificationsAsReadMutation] = useMutation(
    MARK_NOTIFICATIONS_AS_READ_MUTATION
  );
  const { sport } = useSportContext();

  const markNotificationsAsRead = useCallback(
    async (notifications?: { id: string }[]) => {
      const notificationIds = notifications
        ?.map(({ id }) => idFromObject(id))
        .filter(Boolean);

      await markNotificationsAsReadMutation({
        variables: {
          input: {
            notificationIds,
          },
          sport,
          notificationCategories,
          importantNotificationCategories,
        },
      });
    },
    [markNotificationsAsReadMutation, sport]
  );
  return markNotificationsAsRead;
};
